import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    ColumnDef,
    RowSelectionState,
    SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable
} from "@tanstack/react-table"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { ReactNode, useEffect, useState } from "react"
import { FormattedMessage } from "react-intl"
import { useDispatch, useSelector } from "react-redux"
import { ClipLoader } from "react-spinners"
import Guard from "../../../../role/Guard"
import { InventoryLocationAction } from "../../../../state/inventory/location/actions"
import { InventoryLocationSelector } from "../../../../state/inventory/location/selectors"
import {
    FiltersState,
    JUSTIFY_CONTENT_MAP,
    SortingState as ListingSortingState,
    ObjectType,
    ObjectTypeConfig,
    View
} from "../../../../state/listing/generic-listing"
import { ColumnDefinition, TableRowData } from "../../../../state/listing/generic-listing/column-definitions"
import SearchTotals from "../../../../state/search/SearchTotals"
import Checkbox from "../../../checkbox/checkbox.component"
import Icon from "../../../icon/icon.component"
import LoadingSpinner from "../../../spinner/loading.component"
import { SelectOption } from "../../../types/select/types"
import NoResult from "../../noresult.component"
import ColumnSelectionModal from "../header-selection/ColumnSelectionModal"
import {
    getDefaultSortId,
    getNestedSortingFilters,
    mapColumnIdToSortId,
    mapListingSortingStateToTableSortingState
} from "../utils"
import "./ListingTable.scss"
import { mapColumnDefinitionToTableColumn } from "./mappers"

interface OwnProps {
    config: ObjectTypeConfig
    data: TableRowData[]
    totals: SearchTotals | undefined
    totalItemsCount: number
    selectableRows: boolean
    view: View,
    sorting: ListingSortingState,
    additionalOptions: { [key: string]: SelectOption<string>[] }
    loadingState: { [key: string]: boolean }
    onSortingChange: (sortField: string, asc: boolean, nestedSortFilters?: { [key: string]: string }) => void
    onOpenBulkModal: (type: string, selectionIds: string[], isAllPagesSelected: boolean) => void
    onApplyView: (view: View) => void
    filters?: FiltersState
    disableEdit?: boolean
    noResultElement?: JSX.Element
}

enum CellType {
    HEADER = "header",
    CELL = "cell",
    FOOTER = "footer",
}

const GenericListingTable = (props: OwnProps) => {
    const inventoryLocations = useSelector(InventoryLocationSelector.getListing)
    const dispatch = useDispatch()
    const [data, setData] = useState<TableRowData[]>([])
    const [columns, setColumns] = useState<ColumnDef<TableRowData>[]>([])
    const [sorting, setSorting] = useState<SortingState>(mapListingSortingStateToTableSortingState(props.config.objectType, props.sorting))
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
    const [isAllPagesSelected, setIsAllPagesSelected] = useState<boolean>(false)

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            rowSelection,
        },
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        manualSorting: true,
        columnResizeMode: "onChange",
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        meta: {
            isAllPagesSelected,
            totalItemsCount: props.totalItemsCount,
            additionalOptions: props.additionalOptions,
            inventoryLocations,
            filters: props.filters,
        }
    })
    const selectColumn: ColumnDef<TableRowData> = {
        id: "select",
        enableResizing: false,
        size: 10,
        header: () => (
            <Checkbox
                {...{
                    id: "select-all-checkbox",
                    checked: table.getIsAllPageRowsSelected(),
                    onChange: table.getToggleAllPageRowsSelectedHandler(),
                    propagateEvents: true,
                }}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                {...{
                    checked: row.getIsSelected(),
                    disabled: !row.getCanSelect(),
                    onChange: row.getToggleSelectedHandler(),
                    propagateEvents: true,
                }}
            />
        ),
        meta: {
            align: "left",
            sortable: false,
        },
    }

    const viewEditColumn: ColumnDef<TableRowData> = {
        id: "viewEdit",
        enableResizing: false,
        size: 5,
        header: () => (
            <ColumnSelectionModal
                config={props.config}
                view={props.view}
                onApply={props.onApplyView}
            />
        ),
        meta: {
            align: "right",
            sortable: false,
        },
    }

    useEffect(() => {
        const visibleColumnDefs: ColumnDef<TableRowData>[] = []

        for (const viewColumn of props.view.columns) {
            const columnDef = props.config.columnDefinitions.find(definition => definition.id === viewColumn.attributeId)
            if (columnDef) {
                visibleColumnDefs.push(
                    mapColumnDefinitionToTableColumn(
                        columnDef,
                        props.config.headerMessages,
                        props.totals,
                        viewColumn.showTotal ?? false,
                        props.config.customFieldHeaders,
                    )
                )
            }
        }

        setColumns([
            ...(props.selectableRows ? [selectColumn] : []),
            ...visibleColumnDefs,
            ...(!props.disableEdit ? [viewEditColumn] : []),
        ])
    }, [props.config, props.totals, props.view])

    useEffect(() => {
        table.resetRowSelection()
        setData([...(props.data || [])])
    }, [props.data])

    useEffect(() => {
        table.resetRowSelection()
        if (sorting.length) {
            props.onSortingChange(
                mapColumnIdToSortId(props.config.objectType, sorting[0].id),
                !sorting[0].desc,
                getNestedSortingFilters(props.config.objectType, sorting[0].id)
            )
        } else {
            props.onSortingChange(getDefaultSortId(props.config.objectType), false)
        }
    }, [sorting])

    useEffect(() => {
        if (isAllPagesSelected) {
            setIsAllPagesSelected(false)
        }
    }, [table.getSelectedRowModel()])

    useEffect(() => {
        const isInventoryListing = [
            ObjectType.PRODUCTS,
            ObjectType.MATRICES,
            ObjectType.BUNDLES,
        ].includes(props.config.objectType)

        if (AppContext.isInventoryEnabled() && isInventoryListing) {
            dispatch(InventoryLocationAction.fetchListing())
        }
    }, [])

    const getCellClasses = (
        id: string,
        index: number,
        type: CellType,
        columnDefinition: ColumnDefinition | void
    ): string => {
        const classes: string[] = []

        // First cell of the footer is always sticky as it contains the totals icon
        if (index === 0 && type === CellType.FOOTER) {
            classes.push("sticky", "sticky-left")
        }

        // Row selection checkboxes are sticky
        if (id === "select") {
            classes.push("sticky", "sticky-left")
        }

        // In the case of the viewEdit column, we only want to make the header cell sticky instead of the whole column
        if (id === "viewEdit" && type === CellType.HEADER) {
            classes.push("sticky", "sticky-right")
        }

        if (type !== CellType.FOOTER && isColumnLoading(columnDefinition)) {
            classes.push("loading")
        }

        return [...new Set(classes)].join(" ")
    }

    const isColumnLoading = (columnDefinition: ColumnDefinition | void): boolean => {
        if (columnDefinition) {
            const loadingKey = columnDefinition.dataAttributes?.loadingKey

            if (loadingKey) {
                return props.loadingState[loadingKey] ?? false
            }
        }

        return false
    }

    const getBulkActionsMenu = (): ReactNode[] => {
        const menuItems: ReactNode[] = []

        props.config.bulkActionDefinitions.forEach(bulkActionDefinition => {
            const isGuardAuthorized = bulkActionDefinition.guard ? Guard.isAuthorized(bulkActionDefinition.guard) : true
            const isPermissionAuthorized = bulkActionDefinition.permission ?? true

            if (isGuardAuthorized && isPermissionAuthorized) {
                menuItems.push(
                    <a
                        key={bulkActionDefinition.id}
                        className="dropdown-item"
                        href="#"
                        onClick={() => props.onOpenBulkModal(
                            bulkActionDefinition.id,
                            table.getSelectedRowModel().rows.map(row => row.original.id),
                            table.options.meta?.isAllPagesSelected ?? false,
                        )}
                    >
                        <span className={bulkActionDefinition.type ? `text-${bulkActionDefinition.type}` : ""}>
                            <Icon value={bulkActionDefinition.icon} className="mr-2" />
                            <FormattedMessage {...bulkActionDefinition.message} />
                        </span>
                    </a>
                )
            }
        })

        return menuItems
    }

    // Thethers the bulk actions dropdown to the selection checkbox
    const checkboxOffset = document.getElementById("select-all-checkbox")?.offsetParent as HTMLElement

    return (
        <div className="wrapper">
            {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
                <div
                    id="bulk-actions-dropdown"
                    className="btn-group position-absolute bg-light"
                    style={{
                        top: `${checkboxOffset?.offsetTop || 0}px`,
                        left: `${(checkboxOffset?.offsetLeft || 0) + 25}px`,
                    }}
                >
                    <button
                        type="button"
                        className="py-0 btn btn-link btn-sm"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                    >
                        <Icon value="layer-group" className="mr-1" />
                        <FormattedMessage
                            id="common.listing.headers.items_selected_label"
                            defaultMessage="{count} item(s) selected"
                            values={{
                                count: table.options.meta?.isAllPagesSelected
                                    ? table.options.meta?.totalItemsCount ?? 0
                                    : table.getSelectedRowModel().rows.length
                            }}
                        />
                    </button>
                    <div className="dropdown-menu">
                        {getBulkActionsMenu()}
                    </div>
                </div>
            )}
            <div className="listing-table">
                {props.loadingState["loadingData"] && (
                    <div className="loader">
                        <LoadingSpinner color="#fff" />
                    </div>
                )}
                {props.data.length === 0 && (
                    !props.noResultElement ?
                        <NoResult className="no-result" noButton /> : props.noResultElement
                )}
                {props.data.length > 0 && (
                    <table>
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header, i) => (
                                        <th
                                            key={header.id}
                                            {...{
                                                className: getCellClasses(
                                                    header.column.id,
                                                    i,
                                                    CellType.HEADER,
                                                    header.column.columnDef.meta?.columnDefinition,
                                                ),
                                                style: {
                                                    width: header.getSize(),
                                                },
                                            }}
                                        >
                                            <div
                                                className="header-container"
                                                style={{
                                                    justifyContent: JUSTIFY_CONTENT_MAP[header.column.columnDef.meta?.align ?? "left"],
                                                    textAlign: header.column.columnDef.meta?.align ?? "left",
                                                }}
                                            >
                                                <div
                                                    className={
                                                        `header-title ${header.column.columnDef.meta?.sortable ? "sortable" : ""}`
                                                    }
                                                    {...{
                                                        onClick: header.column.columnDef.meta?.sortable
                                                            ? header.column.getToggleSortingHandler()
                                                            : undefined,
                                                    }}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                </div>
                                                {isColumnLoading(header.column.columnDef.meta?.columnDefinition) && (
                                                    <ClipLoader color="#c5c5c5" size={15} />
                                                )}
                                                {header.column.columnDef.meta?.sortable && (
                                                    <div className="sorting-indicator">
                                                        {{
                                                            asc: <i className="fa fa-caret-up" />,
                                                            desc: <i className="fa fa-caret-down" />,
                                                        }[header.column.getIsSorted() as string] ?? null}
                                                    </div>
                                                )}
                                            </div>
                                            {header.column.getCanResize() && (
                                                <div
                                                    {...{
                                                        onDoubleClick: () => header.column.resetSize(),
                                                        onMouseDown: header.getResizeHandler(),
                                                        onTouchStart: header.getResizeHandler(),
                                                        className: `resizer ${header.column.getIsResizing() ? "isResizing" : ""}`,
                                                    }}
                                                />
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            {table.getIsAllRowsSelected() && data.length < props.totalItemsCount && (
                                <tr className="selection-header">
                                    <th colSpan={table.getAllColumns().length}>
                                        <p className="mb-0 text-center">
                                            <FormattedMessage
                                                id="generic-listing.page_selected_message"
                                                defaultMessage="All {count} items on this page are selected."
                                                values={{
                                                    count: (
                                                        <strong className="bigger">
                                                            {isAllPagesSelected ? props.totalItemsCount : data.length}
                                                        </strong>
                                                    )
                                                }}
                                            />
                                            {!isAllPagesSelected && (
                                                <button className="btn btn-sm btn-outline-primary ml-2" onClick={() => setIsAllPagesSelected(true)}>
                                                    <FormattedMessage
                                                        id="generic-listing.select_all_pages_btn"
                                                        defaultMessage="Select all {count} items"
                                                        values={{
                                                            count: <strong className="bigger">{props.totalItemsCount}</strong>
                                                        }}
                                                    />
                                                </button>
                                            )}
                                            {isAllPagesSelected && (
                                                <button className="btn btn-sm btn-outline-primary ml-2" onClick={() => setIsAllPagesSelected(false)}>
                                                    <FormattedMessage
                                                        id="generic-listing.clear_selection_btn"
                                                        defaultMessage="Clear selection"
                                                    />
                                                </button>
                                            )}
                                        </p>
                                    </th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map(row => (
                                <tr key={row.id} className={`${row.getIsSelected() ? "selected" : ""}`}>
                                    {row.getVisibleCells().map((cell, i) => (
                                        <td
                                            key={cell.id}
                                            {...{
                                                className: getCellClasses(
                                                    cell.column.id,
                                                    i,
                                                    CellType.CELL,
                                                    cell.column.columnDef.meta?.columnDefinition,
                                                ),
                                                style: {
                                                    width: cell.column.getSize(),
                                                    textAlign: cell.column.columnDef.meta?.align ?? "left"
                                                },
                                            }}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {table.getFooterGroups().map(footerGroup => (
                                <tr key={footerGroup.id}>
                                    {footerGroup.headers.map((header, i) => (
                                        <th
                                            key={header.id}
                                            {...{
                                                className: getCellClasses(
                                                    header.column.id,
                                                    i,
                                                    CellType.FOOTER,
                                                    header.column.columnDef.meta?.columnDefinition,
                                                ),
                                                style: {
                                                    width: header.column.getSize(),
                                                    textAlign: header.column.columnDef.meta?.align ?? "left"
                                                },
                                            }}
                                        >
                                            {i === 0 && (
                                                <FontAwesomeIcon className="ml-1 mr-2" icon="calculator" />
                                            )}
                                            {flexRender(
                                                header.column.columnDef.footer,
                                                header.getContext()
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    )
}

export default GenericListingTable


// WEBPACK FOOTER //
// ./src/common/listing/generic-listing/listing-table/ListingTable.tsx